import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ContentChildren, ElementRef, EventEmitter, HostListener, Input, OnDestroy,
  OnInit, Output,
  QueryList,
  ViewEncapsulation
} from '@angular/core';
import { interval, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ItemDirective } from '../../directives/item.directive';

@Component({
  selector: 'mcrai-carousel',
  template: `<div class="mcrai-carousel__list" [ngStyle]="listStyle">
  <div *ngFor="let item of items" class="mcrai-carousel__item" [ngStyle]="itemStyle">
    <div [ngTemplateOutlet]="item.template"></div>
  </div>
</div>
`,
  styles: [`.mcrai-carousel{display:block;position:relative;overflow:hidden;cursor:-webkit-grab;cursor:grab}.mcrai-carousel__list{display:flex;align-items:stretch;transition:-webkit-transform 250ms;transition:transform 250ms;transition:transform 250ms,-webkit-transform 250ms;transition-timing-function:cubic-bezier(.25,.8,.25,1)}.mcrai-carousel__item{flex:0 0 0;min-width:0;padding-right:20px}.mcrai-carousel__item:last-child{padding-right:0}`],
  // changeDetection: ChangeDetectionStrategy.OnPush,
  preserveWhitespaces: false,
  encapsulation: ViewEncapsulation.None,
  host: {
    '[class.mcrai-carousel]': 'true'
  }
})
export class CarouselComponent implements OnInit, AfterViewInit, OnDestroy {
  private _destroy: Subject<null> = new Subject<null>();

  @ContentChildren(ItemDirective) private _items: QueryList<ItemDirective> = new QueryList<ItemDirective>();
  get items(): QueryList<ItemDirective> {
    return this._items;
  }

  @Input() public activeIndex = 0;

  @Output() public activeIndexChange: EventEmitter<number> = new EventEmitter<number>();

  private _indexLimit = 0;

  get listStyle(): { [key: string]: string } {
    const offset = this.activeIndex * this._items.first.width;

    return {
      transform: `translateX(-${offset}px)`
    }
  }

  get itemStyle(): { [key: string]: string } {
    return {
      flexBasis: `${this._items.first.width}px`
    }
  }

  constructor(
    private _elRef: ElementRef,
    private _changeDetectorRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this._items.notifyOnChanges();
  }

  ngAfterViewInit() {
    this._setIndexLimit();

    this._items.changes
      .pipe(takeUntil(this._destroy))
      .subscribe(() => this._setIndexLimit());

    interval(100)
      .pipe(
        takeUntil(this._destroy)
      )
      .subscribe(() => this._setIndexLimit());
  }

  ngOnDestroy() {
    this._destroy.next();
  }

  @HostListener('panend', ['$event'])
  _onPanEnd(event): void {
    switch (event.direction) {
      case 2:
        if (!this.isEnd()) {
          this.next();
        }
        return;
      case 4:
        if (!this.isStart()) {
          this.prev();
        }
        return;
    }
  }

  private _setIndexLimit(): void {
    const newIndexLimit = this._items.length - this._elRef.nativeElement.clientWidth / this._items.first.width;

    if (this._indexLimit !== newIndexLimit) {
      this._indexLimit = newIndexLimit;
      this._changeDetectorRef.markForCheck();
    }
  }

  private _emitActiveIndex(): void {
    this.activeIndexChange.emit(this.activeIndex);
  }

  public showControls(): boolean {
    return this._indexLimit > 0;
  }

  public isStart(): boolean {
    return this.activeIndex === 0;
  }

  public isEnd(): boolean {
    return this._indexLimit <= this.activeIndex;
  }

  public next(): void {
    this.activeIndex = this.activeIndex + 1 > this._indexLimit ? this._indexLimit : this.activeIndex + 1;

    this._emitActiveIndex();
    this._changeDetectorRef.markForCheck();
  }

  public prev(): void {
    this.activeIndex = Math.floor(this.activeIndex - 1);

    this._emitActiveIndex();
    this._changeDetectorRef.markForCheck();
  }

  public first(): void {
    this.activeIndex = 0;

    this._emitActiveIndex();
    this._changeDetectorRef.markForCheck();
  }

  public last(): void {
    this.activeIndex = this._indexLimit;

    this._emitActiveIndex();
    this._changeDetectorRef.markForCheck();
  }

}
